// Trace overlays -- the reading that runs ON TOP of one open trace.
//
// Everything else in the trace panel DESCRIBES the trace; the review JUDGES
// it: is there anything here worth an analyst's attention? It is a chip that
// becomes a TAB: clicking it opens the saved review's ledger beneath, and the
// chip grows into the panel's header so the two read as one surface.
//
// Review is a disclosure, not a commissioning control. Opening it loads the
// reports the backend already produced; it never launches browser inference.
import { useState } from "react";
import BackendTraceReviewPanel from "./BackendTraceReviewPanel.jsx";

// A shield reads as "this was examined and judged" where a caret only says
// "there is more below" -- and the ledger is the panel's assertion of
// scrutiny, not a disclosure widget. Same stroke idiom as the trace panel's
// other inline glyphs.
function ShieldIcon({ className }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
            <path d="M12 3 4.5 6v6c0 4.5 3.2 7.6 7.5 9 4.3-1.4 7.5-4.5 7.5-9V6L12 3Z" />
            <path d="m9 12 2.2 2.2L15.5 10" />
        </svg>
    );
}

function reviewLabel(s) {
    if (!s) return "open to load";
    if (s.status === "loading") return "loading...";
    if (s.status === "error") return "failed";
    if (s.status === "done") {
        if (!s.reports) return "not reviewed";
        return s.incidents === 0 ? "clean" : `${s.incidents} finding${s.incidents === 1 ? "" : "s"}`;
    }
    return "not reviewed";
}

export default function TraceOverlays({ trace, onJumpToTurn = null, onEvidence = null }) {
    const [open, setOpen] = useState(false);
    const [reviewActivated, setReviewActivated] = useState(false);
    const [review, setReview] = useState(null);

    if (!trace?.turns?.length) return null;

    const loud = review?.status === "done" && review.incidents > 0;
    const label = reviewLabel(review);

    return (
        <div className="grid min-w-0">
            <button
                type="button"
                onClick={() => {
                    if (!open) setReviewActivated(true);
                    setOpen((v) => !v);
                }}
                aria-expanded={open}
                className={[
                    "relative flex w-full min-w-0 cursor-pointer items-center justify-between gap-[6px] px-[9px] py-[5px] text-[11px] leading-[1.2]",
                    // Open, the chip loses its bottom border to merge with the
                    // body below, and takes the body's surface.
                    open
                        ? "border border-b-0 border-[color:var(--line-strong)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] text-[color:var(--ink)]"
                        : "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]",
                ].join(" ")}
            >
                <span className="inline-flex items-center gap-[6px] min-w-0">
                    <ShieldIcon className="h-[12px] w-[12px] shrink-0" />
                    <span>Review</span>
                </span>
                <span className="truncate" style={loud ? { color: "var(--warn, #c08a2d)" } : undefined}>{label}</span>
            </button>
            {/* Mounted on first open and kept mounted while closed, so a
                reopened tab shows the review it already read. */}
            {reviewActivated ? (
                <div
                    hidden={!open}
                    className="border border-t-0 border-[color:var(--line-strong)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] px-[10px] py-[9px] min-w-0"
                >
                    <BackendTraceReviewPanel
                        trace={trace}
                        onJumpToTurn={onJumpToTurn}
                        onEvidence={open ? onEvidence : null}
                        onStatus={setReview}
                    />
                </div>
            ) : null}
        </div>
    );
}
